import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/utils/api'
import { useNotifications } from '@/composables/useNotifications'

export const useAuthStore = defineStore('auth', () => {
  // Состояние
  const user = ref(null)
  const token = ref(localStorage.getItem('dashboard_token'))
  const isLoading = ref(false)
  const loginError = ref('')

  // Computed
  const isAuthenticated = computed(() => !!user.value && !!token.value)

  // Composables
  const { showNotification } = useNotifications()



  // Действия
  const login = async (credentials) => {
    isLoading.value = true
    loginError.value = ''

    try {
      console.log('🔐 Попытка входа в систему...')

      const response = await apiClient.post('/api/auth/login', credentials)
      const data = response.data

      // Сохраняем токен и пользователя
      token.value = data.access_token
      user.value = data.user

      // Сохраняем токен в localStorage
      localStorage.setItem('dashboard_token', data.access_token)

      console.log('✅ Успешная авторизация:', data.user.username)
      showNotification('success', `Добро пожаловать, ${data.user.full_name || data.user.username}!`)

      return true
    } catch (error) {
      console.error('❌ Ошибка авторизации:', error)

      if (error.response?.status === 401) {
        loginError.value = 'Неверные учетные данные'
      } else if (error.response?.data?.detail) {
        loginError.value = error.response.data.detail
      } else {
        loginError.value = 'Ошибка подключения к серверу'
      }

      showNotification('error', loginError.value)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      console.log('🚪 Выход из системы...')

      // Отправляем запрос на сервер (если есть токен)
      if (token.value) {
        try {
          await apiClient.post('/api/auth/logout')
        } catch (error) {
          console.warn('⚠️ Ошибка при выходе на сервере:', error)
        }
      }
    } catch (error) {
      console.warn('⚠️ Ошибка выхода:', error)
    } finally {
      // Очищаем локальное состояние в любом случае
      user.value = null
      token.value = null
      localStorage.removeItem('dashboard_token')

      console.log('✅ Выход выполнен')
      showNotification('info', 'Вы вышли из системы')
    }
  }

  const checkAuthStatus = async () => {
    // Если нет токена, сразу возвращаем false
    if (!token.value) {
      user.value = null
      return false
    }

    try {
      console.log('🔍 Проверка статуса авторизации...')

      const response = await apiClient.get('/api/auth/check')
      const data = response.data

      if (data.authenticated && data.user) {
        user.value = data.user
        console.log('✅ Пользователь авторизован:', data.user.username)
        return true
      } else {
        console.log('❌ Пользователь не авторизован')
        user.value = null
        token.value = null
        localStorage.removeItem('dashboard_token')
        return false
      }
    } catch (error) {
      console.error('❌ Ошибка проверки авторизации:', error)

      // Если ошибка 401, очищаем данные
      if (error.response?.status === 401) {
        user.value = null
        token.value = null
        localStorage.removeItem('dashboard_token')
      }

      return false
    }
  }

  const updateProfile = async (profileData) => {
    try {
      console.log('👤 Обновление профиля...')

      const response = await apiClient.put('/api/auth/me', profileData)
      const updatedUser = response.data

      user.value = updatedUser
      console.log('✅ Профиль обновлен')
      showNotification('success', 'Профиль обновлен успешно')

      return true
    } catch (error) {
      console.error('❌ Ошибка обновления профиля:', error)

      const message = error.response?.data?.detail || 'Ошибка обновления профиля'
      showNotification('error', message)

      return false
    }
  }

  const changePassword = async (passwordData) => {
    try {
      console.log('🔑 Смена пароля...')

      await apiClient.put('/api/auth/me/password', passwordData)

      console.log('✅ Пароль изменен')
      showNotification('success', 'Пароль изменен успешно')

      return true
    } catch (error) {
      console.error('❌ Ошибка смены пароля:', error)

      const message = error.response?.data?.detail || 'Ошибка смены пароля'
      showNotification('error', message)

      return false
    }
  }

  // Инициализация уже не нужна - interceptors настроены в api.js

  return {
    // Состояние
    user,
    token,
    isLoading,
    loginError,

    // Computed
    isAuthenticated,

    // Действия
    login,
    logout,
    checkAuthStatus,
    updateProfile,
    changePassword
  }
})

